import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HyperlinkText, useTextToHyperlink } from './TextToHyperlink';
import { resolvePersonDetailsUrl } from '../utils/personDetailsNavigation';

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .employee-name-link {
      color: inherit !important;
      text-decoration: none !important;
      cursor: pointer;
    }
    .employee-name-link:hover {
      color: #000FFF !important;
      text-decoration: underline !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Clickable person name: resolves /employee or /vendor detail from link metadata
 * (forced list kind, event employee_type, or GET /faces/person/{id}).
 */
export const EmployeeNameHyperlink = ({ text, employees = [], disabled = false, ...props }) => {
  const navigate = useNavigate();
  const { addListener, removeListener, instance, isReady } = useTextToHyperlink();
  const [forceUpdate, setForceUpdate] = React.useState(0);

  React.useEffect(() => {
    if (disabled) return;
    if (isReady) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [disabled, isReady]);

  const navigateToPersonDetails = React.useCallback(async (person) => {
    if (!person?.person_id) return;
    const url = await resolvePersonDetailsUrl(person.person_id, person);
    if (url) {
      navigate(url);
    }
  }, [navigate]);

  React.useEffect(() => {
    if (disabled) return;
    if (!isReady) return;

    employees.forEach((employee) => {
      if (employee.name) {
        removeListener(employee.name);
      }
    });

    employees.forEach((employee) => {
      if (employee.name && employee.person_id) {
        addListener(
          employee.name,
          () => {
            navigateToPersonDetails(employee);
          },
          {
            wholeWord: true,
            caseSensitive: true,
            className: 'employee-name-link',
            style: {
              cursor: 'pointer',
              fontWeight: 'medium',
            },
          }
        );
      }
    });

    return () => {
      employees.forEach((employee) => {
        if (employee.name) {
          removeListener(employee.name);
        }
      });
    };
  }, [employees, addListener, removeListener, navigate, isReady, forceUpdate, navigateToPersonDetails, disabled]);

  const handleClick = React.useCallback((e) => {
    if (disabled) return;
    if (!isReady || employees.length === 0) return;

    const employee = employees[0];
    if (employee?.name && employee?.person_id) {
      e.preventDefault();
      navigateToPersonDetails(employee);
    }
  }, [employees,isReady, disabled, isReady, navigateToPersonDetails]);

  if (disabled) {
    return (
      <span
        {...props}
        style={{
          color: 'inherit',
          textDecoration: 'none',
          cursor: 'inherit',
          ...(props.style || {}),
        }}
      >
        {text}
      </span>
    );
  }

  if (!isReady) {
    return (
      <span
        {...props}
        onClick={handleClick}
        style={{
          cursor: employees.length > 0 ? 'pointer' : 'default',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <HyperlinkText
      text={text}
      hyperlinkInstance={instance}
      fallbackProps={{
        onClick: handleClick,
      }}
      {...props}
    />
  );
};

export default EmployeeNameHyperlink;
